// Version 4 des Manifests (Grafik-Lead, 18.10.2026 nachts, Vorschlag fürs Gedränge; Fabi entscheidet): ruhige Leisten,
// kleine Klassenzeichen vorn am Ring, kleine Zustandszeichen rechts neben der Leiste statt Wörtern und Bögen am Ring,
// von wo ein Sturmwesen angreift (K35, K40, K45). Fehlt ein Bild, bleibt es beim bisherigen Zeichnen.
import { combatRules } from '../content/index.ts';
import { FrameFlag, FrameFlag2 } from '../sim/index.ts';
import { blit, drawStrip, fxSeconds, image, sprites, sprite, type ArtSprite, type CalmBarSpec } from './art.ts';
import { pointOf, type FigureGeo } from './figures.ts';
import type { DrawUnit } from './render.ts';

/** Die Maße der ruhigen Leiste, wenn der Grafik-Lead sie geliefert hat */
export const calmBar = (): CalmBarSpec | null => sprite('marker', 'leiste-muster')?.bar ?? null;

/** Innere Breite in Bildpixeln: 2 mal gerundet (3 mal radius plus 5) halbe, 10 bis 28 */
export const calmWidth = (radius: number): number => Math.min(28, Math.max(10, 2 * Math.round((3 * radius + 5) / 2)));

export type CalmLayout = { x0: number; y0: number; w: number; h: number; k: number; inner: number };

/** Wo die Leiste liegt (Gerätepixel): untere Kante aboveHead Bildpixel über dem Punkt kopf, mittig über dem Fußpunkt */
export function calmLayout(u: DrawUnit, g: FigureGeo, s: number, spec: CalmBarSpec): CalmLayout {
  const k = g.k;
  const inner = calmWidth(u.radius);
  const w = (inner + 2 * spec.border) * k;
  const h = spec.height * k;
  const head = pointOf(g, 'kopf')?.y ?? g.top;
  const y0 = Math.max(s * 0.3, Math.round(head - spec.aboveHead * k - h));
  return { x0: Math.round(g.x - w / 2), y0, w, h, k, inner };
}

/** Die ruhige Leiste: Rahmen ohne Eckpixel, zwei Reihen Leben, ein dunkler Abstand, eine Reihe Mut */
export function drawCalmBar(ctx: CanvasRenderingContext2D, u: DrawUnit, l: CalmLayout, spec: CalmBarSpec): void {
  const { x0, y0, w, h, k, inner } = l;
  const c = spec.colors;
  const b = spec.border * k;
  ctx.save();
  ctx.fillStyle = c.border;
  if (spec.cornersCut) {
    ctx.fillRect(x0 + b, y0, w - 2 * b, h);
    ctx.fillRect(x0, y0 + b, w, h - 2 * b);
  } else ctx.fillRect(x0, y0, w, h);
  const ix = x0 + b;
  const iw = inner * k;
  // Leben: leer, dann was eben verloren ging (blass), dann das Leben, rechts daran das Schild, alles gestaucht
  const ly = y0 + b;
  const lh = spec.lifeHeight * k;
  const shield = Math.max(0, u.shield ?? 0);
  const total = Math.max(u.maxHp, u.hp + shield);
  const px = (v: number) => Math.round((iw * Math.max(0, v)) / total);
  const life = px(u.hp);
  const lost = u.ghostHp !== undefined && u.ghostHp > u.hp ? Math.min(iw - life, px(u.ghostHp) - life) : 0;
  const shieldW = Math.min(iw - life, px(shield));
  const low = u.hp / u.maxHp < c.lowBelow;
  const rows = (pair: [string, string], x: number, width: number) => {
    if (width <= 0) return;
    for (let r = 0; r < spec.lifeHeight; r++) {
      ctx.fillStyle = r < spec.lifeHeight / 2 ? pair[0] : pair[1];
      ctx.fillRect(x, ly + r * k, width, k);
    }
  };
  ctx.fillStyle = c.lifeEmpty;
  ctx.fillRect(ix, ly, iw, lh);
  rows(low ? c.lifeLow : c.life, ix, life);
  if (lost > 0) rows(c.lost, ix + life, lost);
  if (shieldW > 0) rows(c.shield, ix + life, shieldW);
  // Mut: unter dem Abstand, voll in der hellen Farbe, wenn der Kniff bereit ist
  const my = ly + lh + spec.gapHeight * k;
  const mh = spec.mutHeight * k;
  const max = u.mutMax ?? combatRules.mut.max;
  const share = Math.max(0, Math.min(1, u.mut / max));
  ctx.fillStyle = c.mutEmpty;
  ctx.fillRect(ix, my, iw, mh);
  ctx.fillStyle = share >= 1 - 1e-6 ? c.mutFull : c.mut;
  ctx.fillRect(ix, my, Math.round(iw * share), mh);
  ctx.restore();
}

/** Das kleine Klassenzeichen vorn auf dem Ring (Punkt ring-vorn: unterster Punkt des Rings) */
export function drawSmallClass(ctx: CanvasRenderingContext2D, u: DrawUnit, g: FigureGeo, ground: number): boolean {
  if (!u.friendClass || (u.kind !== 'friend' && u.kind !== 'infested')) return false;
  return drawStrip(ctx, 'class', `${u.friendClass}-klein`, 0, g.x, ground + g.ringRy, g.k);
}

/** Gibt es kleine Zeichen? Dann fallen die großen über dem Kopf weg (rotes Kreuz der Schwachstelle, Sterne beim Benommen) */
export const hasSmallSigns = (): boolean => !!sprite('status', 'benommen-klein');

type Small = { id: string; frame: number };

/**
 * Kleine Zustandszeichen rechts neben der Leiste, höchstens drei, kleinste order zuerst. Was die Figur schon selbst
 * zeigt (Bewegungen fürs Einrollen, Kreiseln, Eingraben, den Bau; die Wunde als Pflaster am Körper), steht nicht in der
 * Reihe. Der Schützling der Leibwache trägt sein Zeichen vorn. Gibt die rechte Kante der Reihe zurück.
 */
export function drawSmallStatus(ctx: CanvasRenderingContext2D, u: DrawUnit, g: FigureGeo, left: number, mid: number, T: number, shows: Set<string>): number {
  const list: (Small & { order: number })[] = [];
  for (const sp of sprites()) {
    if (sp.kind !== 'status' || !sp.flag || !sp.id.endsWith('-klein')) continue;
    if (shows.has(sp.flag)) continue;
    // K46: Zustände des Übungsufers (steckt fest, rutscht …) stehen in der zweiten Bitmaske
    const f1 = (FrameFlag as Record<string, number>)[sp.flag];
    const f2 = (FrameFlag2 as Record<string, number>)[sp.flag];
    const on = sp.flag === 'wounded' ? !!u.wounded : f1 !== undefined ? (u.flags & f1) !== 0 : f2 !== undefined && ((u.flags2 ?? 0) & f2) !== 0;
    if (on) list.push({ id: sp.id, frame: 0, order: sp.order ?? 50 });
  }
  // S13 Pfote halten (Runde 7): das kleine Herz, solange Otter und Freund nah beieinander stehen (kein flag im Bild)
  const paw = sprite('status', 'pfote-halten-klein');
  if (u.pawHeld && paw) list.push({ id: paw.id, frame: 0, order: paw.order ?? 22 });
  if (u.warded && sprite('status', 'leibwache-schuetzling')) list.push({ id: 'leibwache-schuetzling', frame: pulseFrame(sprite('status', 'leibwache-schuetzling')!, T + u.uid * 0.7), order: 0 });
  list.sort((a, b) => a.order - b.order);
  let x = left + g.k;
  for (const s of list.slice(0, 3)) {
    const sp = sprite('status', s.id)!;
    const [fw, fh] = sp.frameSize ?? sp.size ?? [7, 7];
    const [cx, cy] = sp.center ?? [Math.floor(fw / 2), Math.floor(fh / 2)];
    drawStrip(ctx, 'status', s.id, s.frame, x + (cx + 0.5) * g.k, mid - (fh * g.k) / 2 + (cy + 0.5) * g.k, g.k);
    x += (fw + 1) * g.k;
    // F32: die Zahl beim Gift steht daneben
    if (s.id === 'gift-klein' && (u.poison ?? 0) > 0) {
      ctx.save();
      ctx.font = `700 ${Math.max(9, g.k * 5)}px system-ui, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = Math.max(1, g.k * 0.6);
      ctx.strokeStyle = 'rgba(10, 20, 8, 0.85)';
      ctx.fillStyle = '#b7e39f';
      const n = String(u.poison);
      ctx.strokeText(n, x, mid);
      ctx.fillText(n, x, mid);
      x += ctx.measureText(n).width + g.k;
      ctx.restore();
    }
  }
  return x;
}

/** Ein Zeichen, das einmal durchläuft und dann pauseMs ruht (Lichtstreif am Schützling) */
function pulseFrame(sp: ArtSprite, T: number): number {
  const run = fxSeconds(sp);
  const cycle = run + (sp.pauseMs ?? 0) / 1000;
  if (cycle <= 0 || !sp.ms) return 0;
  const a = ((T % cycle) + cycle) % cycle;
  return a < run ? Math.min((sp.frames ?? 1) - 1, Math.floor((a * 1000) / sp.ms)) : 0;
}

/**
 * bedroht-<rx>: je Angreifer ein Bogen knapp außerhalb des Rings, auf der Seite, von der er kommt. Der nächste zuerst;
 * jeder bekommt die innerste Reihe, in der kein anderer Bogen zu nah liegt (drei Richtungen, hinten vier, Version 6),
 * sonst die äußerste. Version 6 (Fabi, Runde 8: der Bogen von oben soll nicht durch die eigene Figur gehen): Jedes Bild
 * wird an splitRow geteilt; der obere Teil liegt hinten am Ring und kommt mit dem Ring unter die eigene Figur (part
 * back), der untere nach allen Figuren (part front). Gibt false zurück, wenn es die Bilder nicht gibt.
 */
export function drawThreatArcs(ctx: CanvasRenderingContext2D, u: DrawUnit, g: FigureGeo, ground: number, part: 'back' | 'front' | 'all' = 'all'): boolean {
  const arcs = sprites().filter((sp) => sp.kind === 'marker' && sp.id.startsWith('bedroht-') && sp.radius !== undefined);
  if (!arcs.length) return false;
  const sp = arcs.reduce((a, b) => (Math.abs(b.radius! - u.radius) < Math.abs(a.radius! - u.radius) ? b : a));
  const img = image(sp.file);
  const dirs = sp.angles?.length ?? 16;
  const rows = sp.rows ?? 3;
  const from = [...(u.attackerFrom ?? [])].sort((a, b) => Math.hypot(a.x - u.x, a.y - u.y) - Math.hypot(b.x - u.x, b.y - u.y));
  const taken: { dir: number; row: number }[] = [];
  const apart = (a: number, b: number) => Math.min((a - b + dirs) % dirs, (b - a + dirs) % dirs);
  /** Richtung 9 bis 15 (von 16) liegt hinten, oberhalb der Figur */
  const back = (d: number) => d > dirs / 2 && d < dirs;
  ctx.save();
  ctx.globalAlpha *= (sp.alpha ?? 0.85) * (1 - (u.pose?.fade ?? 0));
  for (const a of from) {
    const deg = (Math.atan2(a.y - u.y, a.x - u.x) * 180) / Math.PI;
    const dir = ((Math.round(deg / (360 / dirs)) % dirs) + dirs) % dirs;
    let row = rows;
    for (let r = 1; r <= rows; r++) {
      if (!taken.some((t) => t.row === r && apart(t.dir, dir) < (back(t.dir) && back(dir) ? 4 : 3))) {
        row = r;
        break;
      }
    }
    taken.push({ dir, row });
    const frame = (row - 1) * dirs + dir;
    const split = sp.splitRow;
    if (part === 'all' || split === undefined || !img || !sp.frameSize) {
      if (part !== 'back' || split === undefined) drawStrip(ctx, 'marker', sp.id, frame, g.x, ground, g.k);
      continue;
    }
    const [fw, fh] = sp.frameSize;
    const c = sp.center ?? [Math.floor(fw / 2), Math.floor(fh / 2)];
    const [y0, h] = part === 'back' ? [0, split] : [split, fh - split];
    if (h > 0) blit(ctx, img, frame * fw, y0, fw, h, g.x, ground, [c[0], c[1] - 0.5 - y0], g.k);
  }
  ctx.restore();
  return true;
}
